import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { UserService } from "@app/services/user.service";
import { IUserListResponse } from "@app/commons/types/interfaces/users.interfaces";
import { ViewConfig } from "@app/commons/utils/view-config";
import {
  NgbModal,
  NgbModalConfig,
  NgbModalRef,
} from "@ng-bootstrap/ng-bootstrap";
import { getErrorMessage, getRoleDisplayName } from "@app/commons/utils/utils";
import { SnackbarService } from "@app/services/snackbar.service";
import { DialogComponent } from "@app/components/dialog/dialog.component";
import { AuthService } from "@app/services/auth.service";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { UserRoleEnum } from "@app/commons/types/enums/user.enum";
import { CustomEventHandler } from "@app/commons/utils/event-handler";
import { emailValidator, noWhitespace } from "@app/commons/utils/form-utility";
import { Subscription } from "rxjs";
@Component({
  selector: "app-user-list",
  templateUrl: "./user-list.component.html",
  styleUrl: "./user-list.component.scss",
})
export class UserListComponent implements OnInit {
  modalService = inject(NgbModal);
  users: any[] = [];
  collectionSize = 0;
  viewConfig = new ViewConfig();

  createUserModalRef: NgbModalRef | undefined;
  createUserViewConfig = new ViewConfig();
  createUserForm: FormGroup = new FormGroup({});
  createUserFormSubmitted = false;

  getRoleDisplayName = getRoleDisplayName;
  userRoleEnum = UserRoleEnum;
  selectedUser: any = {};

  @ViewChild("content") createUserTempRef: any;
  constructor(
    private userService: UserService,
    private authService: AuthService,
    config: NgbModalConfig,
    private snackBarService: SnackbarService,
    private eventHandler: CustomEventHandler,
    private cdr: ChangeDetectorRef,
  ) {
    config.backdrop = "static";
    config.keyboard = false;
  }

  emailChangeSubscription!: Subscription;

  ngOnInit(): void {
    this.getUsers();
    this.createUserForm = new FormGroup({
      firstName: new FormControl("", [Validators.required, noWhitespace]),
      lastName: new FormControl("", [Validators.required, noWhitespace]),
      email: new FormControl("", [
        Validators.required,
        noWhitespace,
        emailValidator,
      ]),
      role: new FormControl("", [noWhitespace, Validators.required]),
      active: new FormControl(true),
    });
    this.eventHandler.addListener("open-create-user", this.open.bind(this));
    this.emailChangeSubscription = this.createUserForm.controls[
      "email"
    ].valueChanges.subscribe((value) => {
      this.createUserViewConfig.setError(null);
    });
  }

  ngOnDestroy(): void {
    this.emailChangeSubscription.unsubscribe();
  }

  onActionMenuOpen(isOpen: boolean, index: number) {
    if (!isOpen) {
      return;
    }
    setTimeout(() => {
      const firstItem = document.getElementById(
        `action-menu-first-${index}`
      ) as HTMLButtonElement | null;
      firstItem?.focus();
    }, 0);
  }

  hasEmailError() {
    return this.createUserViewConfig.error?.length ? true : false;
  }

  getUsers() {
    this.viewConfig.setLoading(true);
    this.userService.getUsers().subscribe({
      next: (result) => {
        this.viewConfig.setLoading(false);
        this.users = result;
        this.collectionSize = result.length;
      },
      error: (e) => {
        this.viewConfig.setError(getErrorMessage(e));
      },
    });
  }

  activateOrInactivate(active: boolean, userId: string) {
    const payload = {
      action: "disable",
    };
    if (active) {
      payload.action = "activate";
    }
    const modalRef = this.modalService.open(DialogComponent, {
      backdrop: true,
    });
    modalRef.componentInstance.alert = {
      message: "",
      type: "",
    };
    modalRef.componentInstance.title = `${
      active ? "Activate" : "Deactivate"
    } User`;
    modalRef.componentInstance.message = `Are you sure you want to <strong>${
      active ? "Activate" : "Deactivate"
    }</strong> this user?`;
    modalRef.componentInstance.buttons = [
      {
        id: 0,
        type: "outline-dark",
        class: "ind-button-md",
        label: "Cancel",
        onClick: () => {
          modalRef.close();
        },
      },
      {
        id: 1,
        type: "primary",
        class: "ind-button-md",
        label: "Ok",
        onClick: () => {
          this.viewConfig.setLoading(true);
          this.authService.activateAction(userId, payload).subscribe({
            next: (result: IUserListResponse) => {
              this.getUsers();
              modalRef.componentInstance.alert = {
                message: `User ${
                  active ? "Activated" : "Deactivated"
                } Successfully`,
                type: "success",
              };
              modalRef.componentInstance.message = "";
              modalRef.componentInstance.buttons = [];
              setTimeout(() => {
                modalRef.close();
              }, 2000);
            },
            error: (e) => {
              modalRef.componentInstance.alert = {
                message: getErrorMessage(e),
                type: "danger",
              };
              // this.viewConfig.setError(getErrorMessage(e));
            },
          });
        },
      },
    ];
  }

  refreshData() {
    this.getUsers();
  }

  openResetPasswordModal(userId: string, userEmail: string) {
    const modalRef = this.modalService.open(DialogComponent, {
      backdrop: true,
    });
    modalRef.componentInstance.alert = {
      message: "",
      type: "",
    };
    modalRef.componentInstance.title = `Reset password`;
    modalRef.componentInstance.message = `Are you sure you want to send a reset password for this user?`;
    modalRef.componentInstance.buttons = [
      {
        id: 0,
        type: "outline-dark",
        class: "ind-button-md",
        label: "Cancel",
        onClick: () => {
          modalRef.close();
        },
      },
      {
        id: 1,
        type: "primary",
        class: "ind-button-md",
        label: "Ok",
        onClick: () => {
          this.viewConfig.setLoading(true);
          this.authService.resetPassword(userEmail);
          this.viewConfig.setLoading(false);
          modalRef.componentInstance.alert = {
            message: `Reset password sent to ${userEmail}`,
            type: "success",
          };
          modalRef.componentInstance.message = "";
          modalRef.componentInstance.buttons = [];
          setTimeout(() => {
            modalRef.close();
          }, 2000);
        },
      },
    ];
  }

  makeAdmin(userId: string) {
    this.viewConfig.setLoading(true);
    this.authService
      .update(userId, {
        admin: true,
      })
      .subscribe((data) => {
        this.viewConfig.setLoading(false);
        this.refreshData();
      });
  }

  makeStaffUser(userId: string) {
    this.viewConfig.setLoading(true);
    this.authService
      .update(userId, {
        admin: false,
      })
      .subscribe((data) => {
        this.viewConfig.setLoading(false);
        this.refreshData();
      });
  }

  getRole(claims: any) {
    return claims?.admin === true
      ? getRoleDisplayName(UserRoleEnum.ADMIN)
      : getRoleDisplayName(UserRoleEnum.USER);
  }

  getAddUserHeader() {
    return this.selectedUser.id ? "Edit User" : "Add User";
  }

  /** Focus order matches visible fields: edit mode omits email/role (not in DOM). */
  private focusFirstInvalidCreateUserField(): void {
    const names: string[] = ["firstName", "lastName"];
    if (!this.selectedUser?.id) {
      names.push("email", "role");
    }
    for (const name of names) {
      const control = this.createUserForm.get(name);
      if (control?.invalid) {
        const el = document.getElementById(name) as
          | HTMLInputElement
          | HTMLSelectElement
          | null;
        el?.focus();
        break;
      }
    }
  }

  onCreateUser() {
    this.createUserFormSubmitted = true;
    if (this.createUserForm.invalid) {
      this.cdr.detectChanges();
      queueMicrotask(() => this.focusFirstInvalidCreateUserField());
      return;
    }

    if (this.selectedUser.id) {
      this.onCreateUserConfirm();
      return;
    }

    const modalRef = this.modalService.open(DialogComponent, {
      backdrop: true,
    });
    modalRef.componentInstance.alert = {
      message: "",
      type: "",
    };
    modalRef.componentInstance.title = `Are you Sure?`;
    modalRef.componentInstance.message = `Are you sure you want to create this user?`;
    modalRef.componentInstance.buttons = [
      {
        id: 0,
        type: "outline-dark",
        class: "ind-button-md",
        label: "Cancel",
        onClick: () => {
          modalRef.close();
        },
      },
      {
        id: 1,
        type: "primary",
        class: "ind-button-md",
        label: "Save",
        onClick: () => {
          modalRef.close();
          this.onCreateUserConfirm();
        },
      },
    ];
  }

  onCreateUserConfirm() {
    this.createUserViewConfig.setLoading(true);
    if (this.selectedUser["id"]) {
      this.userService
        .update(this.selectedUser.id, {
          firstName: this.createUserForm.value.firstName,
          lastName: this.createUserForm.value.lastName,
        })
        .subscribe({
          next: () => {
            this.createUserModalRef?.close();
            this.resetCreateUserForm();
            this.getUsers();
            this.snackBarService.show("User details updated successfully");
          },
        });
    } else {
      this.userService.create(this.createUserForm.value).subscribe({
        next: () => {
          this.createUserViewConfig.setLoading(false);
          this.createUserModalRef?.close();
          this.snackBarService.show("User created successfully");
          this.authService.resetPassword(this.createUserForm.value.email);
          this.getUsers();
          this.resetCreateUserForm();
        },
        error: (e) => {
          this.createUserViewConfig.setError(getErrorMessage(e));
        },
      });
    }
  }

  resetCreateUserForm() {
    this.createUserFormSubmitted = false;
    this.selectedUser = {};
    this.createUserViewConfig.setDefault();
    this.createUserForm.reset();
    this.createUserForm.patchValue({
      role: "",
      active: true,
    });
  }

  get userForm(): { [key: string]: AbstractControl } {
    return this.createUserForm.controls;
  }

  open() {
    this.resetCreateUserForm();
    this.createUserModalRef = this.modalService.open(this.createUserTempRef, {
      centered: true,
    });
  }

  getSaveButtonText() {
    let label = this.selectedUser.id ? "Update" : "Create";
    if (this.createUserViewConfig.loading) {
      label = this.selectedUser.id ? "Updating..." : "Creating...";
    }
    return label;
  }

  edit(user: any) {
    this.selectedUser = user;
    this.createUserForm.patchValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
    });
    this.createUserModalRef = this.modalService.open(this.createUserTempRef, {
      centered: true,
    });
  }

  resetMfa(user: any) {
    this.createUserViewConfig.setLoading(true);
    this.authService
      .resetMfaForUser(user.identityPlatformId)
      .subscribe((data) => {
        this.createUserViewConfig.setLoading(false);
        this.snackBarService.show(`User's MFA has been reset successfully`);
      });
  }
}
